import {
  Alert,
  Box,
  Button,
  Chip,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material"
import { InfoOutlined, Refresh } from "@mui/icons-material"
import { FC } from "react"
import { useTranslation } from "react-i18next"

/**
 * Live-monitoring controls for the migration page (Items 2, 3, 9, DEV-193):
 *  - Item 2: auto-refresh toggle + "updated Xs ago" freshness readout.
 *  - Item 3: error banner + retry — a failed super-admin/IAM-gated fetch must
 *    look DIFFERENT from an empty result (empty grid = "nothing yet", banner =
 *    "the fetch broke").
 *  - Item 9: ingestion-health indicator. The `Sstv2InvalidEnvelope` metric is
 *    emitted by the SQS consumer to CloudWatch; the wfe has no read path for it,
 *    so we degrade honestly to "unavailable" rather than fabricate a number.
 */

const formatAgo = (ms: number): string => {
  if (!Number.isFinite(ms) || ms < 0) return "—"
  const s = Math.floor(ms / 1000)
  if (s < 60) return `${s}s`
  const m = Math.floor(s / 60)
  if (m < 60) return `${m}m`
  const h = Math.floor(m / 60)
  return `${h}h ${m % 60}m`
}

export const Sstv2MigrationControls: FC<{
  autoRefresh: boolean
  onToggleAutoRefresh: (v: boolean) => void
  lastUpdatedMs?: number
  nowMs: number
  isFetching: boolean
  onRefresh: () => void
  /** i18n labels of the queries that failed (empty = no error). */
  errorLabels: string[]
  onRetry: () => void
}> = ({
  autoRefresh,
  onToggleAutoRefresh,
  lastUpdatedMs,
  nowMs,
  isFetching,
  onRefresh,
  errorLabels,
  onRetry,
}) => {
  const { t } = useTranslation()
  const agoMs = lastUpdatedMs ? nowMs - lastUpdatedMs : NaN

  return (
    <Box data-ui="UI-SL2W-252" sx={{ mb: 2.5 }}>
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        flexWrap="wrap"
        rowGap={1}
      >
        <FormControlLabel
          control={
            <Switch
              checked={autoRefresh}
              onChange={(e) => onToggleAutoRefresh(e.target.checked)}
            />
          }
          label={t("migrationAutoRefresh")}
        />
        <Typography variant="caption" color="text.secondary">
          {lastUpdatedMs
            ? t("migrationUpdatedAgo", { ago: formatAgo(agoMs) })
            : t("migrationNeverUpdated")}
        </Typography>
        <Tooltip title={t("migrationRefreshNow")}>
          <span>
            <IconButton size="small" onClick={onRefresh} disabled={isFetching}>
              <Refresh fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Box sx={{ flexGrow: 1 }} />

        <Tooltip title={t("migrationIngestionHealthTip")}>
          <Chip
            size="small"
            variant="outlined"
            icon={<InfoOutlined />}
            label={t("migrationIngestionHealthUnavailable")}
          />
        </Tooltip>
      </Stack>

      {errorLabels.length > 0 && (
        <Alert
          severity="error"
          sx={{ mt: 1.5 }}
          action={
            <Button color="inherit" size="small" onClick={onRetry}>
              {t("retry")}
            </Button>
          }
        >
          {t("migrationLoadError", { queries: errorLabels.join(", ") })}
        </Alert>
      )}
    </Box>
  )
}

export default Sstv2MigrationControls
